import { Inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'

import { CrudService } from '../../services/crud.service'
import { SDKConfiguration, SDK_CONFIGURATION } from '../../models/config.model'
import { SDK_SETTINGS } from '../../consts/config.const'
import { Page } from '../../models/util.model'
import { Replenishment } from './models/replenishment.entity'
import { ReplenishmentSearchParams } from './models/replenishment-search.model'

const MODEL = 'replenishments'
const VERSION = 'v3'

@Injectable({
  providedIn: 'root',
})
export class ReplenishmentsService extends CrudService {
  constructor(
    @Inject(SDK_CONFIGURATION) config: SDKConfiguration,
    http: HttpClient,
  ) {
    super(
      config,
      http,
      `${config.apiUrl}/${SDK_SETTINGS.apiPath}/${VERSION}/${MODEL}`,
    )
  }

  // ============================================================================
  // CRUD Operations
  // ============================================================================

  /**
   * Create a new replenishment
   * @param params - the creation params
   * @returns the observable<Replenishment> for create the replenishment
   */
  create$(replenishment: Partial<Replenishment>): Observable<Replenishment> {
    return this._create$<
      Replenishment,
      {
        product?: { _id: string }
        warehouseId?: string
        priority?: number
        requiredQty?: number
        source?: { _id: string }
        target?: { _id: string }
      }
    >({
      product: replenishment?.product?._id
        ? { _id: replenishment.product._id }
        : undefined,
      warehouseId: replenishment.warehouseId,
      priority: replenishment.priority,
      requiredQty: replenishment.requiredQty,
      source: replenishment.source?._id
        ? { _id: replenishment.source._id }
        : undefined,
      target: replenishment.target?._id
        ? { _id: replenishment.target._id }
        : undefined,
    })
  }

  /**
   * Read a replenishment by ID
   * @param replenishmentId - the replenishment ID
   * @returns the obsevable<Replenishment> for read the replenishment
   */
  read$(replenishmentId: string): Observable<Replenishment> {
    return this._read$<Replenishment>(replenishmentId)
  }

  /**
   * Create or update a replenishment by ID
   * @param replenishmentId - The replenishment ID
   * @param replenishment - The replenishment body to update
   * @returns The observable<Replenishment> for update the replenishment
   */
  upsert$(replenishment: Replenishment): Observable<Replenishment> {
    return this._upsert$<Replenishment>(replenishment, replenishment._id)
  }

  /**
   * Search replenishments by params
   * @param searchParams - the search params
   * @returns the observable
   */
  search$(
    searchParams?: ReplenishmentSearchParams,
    returnAll = false,
  ): Observable<Page<Replenishment>> {
    return this._search$<Replenishment>(searchParams, returnAll)
  }

  /**
   * List products by params
   * @param params - The search params
   * @param returnAll - the returnAll flag
   * @returns The observable<Product[]> for list products
   */
  list$(params?: ReplenishmentSearchParams): Observable<Replenishment[]> {
    return this._list$<Replenishment>(params)
  }

  // ============================================================================
  // Status Management
  // ============================================================================

  /**
   * Manage a replenishment
   * @param replenishmentId - the replenishment ID
   * @returns the observable<Replenishment> for manage the replenishment
   */
  manage$(replenishmentId: string): Observable<Replenishment> {
    return this.http.post<Replenishment>(
      this.buildUrl(replenishmentId, 'manage'),
      {},
    )
  }

  /**
   * Change location of a replenishmnet
   * @param replenishmentId - the replenishment ID
   * @returns the observable<Replenishment> for cancel the replenishment
   */
  cancel$(replenishmentId: string): Observable<Replenishment> {
    return this.http.post<Replenishment>(
      this.buildUrl(replenishmentId, 'cancel'),
      {},
    )
  }

  // ============================================================================
  // Attachments
  // ============================================================================

  uploadUrl(): string {
    return this.buildUrl('blob')
  }
}
